import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import ChurchRoundedIcon from '@mui/icons-material/ChurchRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { getMemberOf } from 'features/memberships/membershipSlice'
import { imageDefaults } from 'components/utils/imageDefaults'
import { useTranslation } from 'react-i18next'

export default function ChurchThreadList() {
  const { t } = useTranslation('messaging')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useSelector((state) => state.auth)
  const { memberOf, getMemberOfLoading, getMemberOfSuccess } = useSelector((state) => state.memberships2)

  useEffect(() => {
    if (!user?.isOrganization && !getMemberOfLoading && !getMemberOfSuccess) dispatch(getMemberOf())
  }, [dispatch, getMemberOfLoading, getMemberOfSuccess, user?.isOrganization])

  const churches = useMemo(() => {
    const source = user?.isOrganization ? [user] : (memberOf || [])
    const seen = new Set()
    return source.map((church) => ({
      id: String(church?._id || church?.id || ''),
      name: church?.fullName || church?.name || t('participants.church'),
      image: church?.profileImg?.url || church?.profileImage?.url || imageDefaults.avatar,
      publicKey: church?.publicKey || null,
    })).filter((church) => {
      if (!church.id || seen.has(church.id)) return false
      seen.add(church.id)
      return true
    })
  }, [memberOf, t, user])

  if (!churches.length) return null

  const openThread = (church) => {
    sessionStorage.setItem(`church-thread:${church.id}`, JSON.stringify(church))
    navigate(`/messages/church/${church.id}`)
  }

  return (
    <Section>
      <SectionTitle>{t('sections.churchThreads')}</SectionTitle>
      <ThreadGroup>
        {churches.map((church) => {
          const selected = location.pathname === `/messages/church/${church.id}`
          return (
            <ThreadRow key={church.id} type='button' $selected={selected} onClick={() => openThread(church)} aria-label={t('churchThread.open', { name: church.name })}>
              <AvatarWrap>
                <Avatar src={church.image} alt='' />
                <ChurchBadge><ChurchRoundedIcon /></ChurchBadge>
              </AvatarWrap>
              <ThreadText><strong>{church.name}</strong><span>{t('churchThread.title')} <LockRoundedIcon /></span></ThreadText>
            </ThreadRow>
          )
        })}
      </ThreadGroup>
      <SectionTitle $messages>{t('sections.messages')}</SectionTitle>
    </Section>
  )
}

const Section = styled.section`width: 100%; padding: 0 4px;`
const SectionTitle = styled.h2`
  margin: ${({ $messages }) => $messages ? '18px 8px 8px' : '4px 8px 8px'};
  color: #7a8589; font-size: 10px; font-weight: 800; letter-spacing: .09em; text-transform: uppercase;
`
const ThreadGroup = styled.div`overflow: hidden; border: 1px solid #e4e8ea; border-radius: 14px; background: white;`
const ThreadRow = styled.button`
  width: 100%; display: flex; align-items: center; gap: 11px; padding: 10px 12px; border: 0; cursor: pointer; text-align: left;
  background: ${({ $selected }) => $selected ? 'rgba(0, 180, 204, .10)' : 'white'};
  &:not(:last-child) { border-bottom: 1px solid #edf0f1; }
  &:hover { background: ${({ $selected }) => $selected ? 'rgba(0, 180, 204, .13)' : '#f5f7f8'}; }
  &:focus-visible { position: relative; z-index: 1; outline: 3px solid rgba(0, 180, 204, .2); outline-offset: -3px; }
`
const AvatarWrap = styled.span`position: relative; flex: 0 0 42px; width: 42px; height: 42px;`
const Avatar = styled.img`width: 42px; height: 42px; display: block; border-radius: 50%; object-fit: cover; background: #e8edef;`
const ChurchBadge = styled.span`
  position: absolute; right: -2px; bottom: -1px; width: 18px; height: 18px; display: grid; place-items: center;
  border: 2px solid white; border-radius: 50%; background: var(--themeColor); color: white;
  svg { font-size: 10px; }
`
const ThreadText = styled.span`
  min-width: 0; display: flex; flex-direction: column; gap: 4px;
  strong { overflow: hidden; color: #344044; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
  span { display: flex; align-items: center; gap: 4px; color: #7b8589; font-size: 11px; }
  svg { font-size: 12px; }
`
